import React, { createContext, useReducer, useEffect, useCallback } from 'react';
import db from '../services/db';
import { sharedDb, recordChange } from '@monorepo/shared';
import { validatePlantRecord } from '../services/validation';
import { v4 as uuidv4 } from 'uuid';

const PlantContext = createContext(null);

const initialState = {
  plants: [],
  species: [],
  loading: true,
  error: null,
};

function plantReducer(state, action) {
  switch (action.type) {
    case 'LOAD_PLANTS_SUCCESS':
      return { ...state, plants: action.payload, loading: false, error: null };
    case 'LOAD_SPECIES_SUCCESS':
      return { ...state, species: action.payload };
    case 'LOAD_PLANTS_ERROR':
      return { ...state, loading: false, error: action.payload };
    case 'ADD_PLANT':
      return { ...state, plants: [...state.plants, action.payload], error: null };
    case 'ADD_SPECIES':
      return { ...state, species: [...state.species, action.payload], error: null };
    case 'UPDATE_SPECIES':
      return {
        ...state,
        species: state.species.map((s) =>
          s.id === action.payload.id ? action.payload : s
        ),
        error: null,
      };
    case 'DELETE_SPECIES':
      return {
        ...state,
        species: state.species.filter((s) => s.id !== action.payload),
        error: null,
      };
    case 'UPDATE_PLANT':
      return {
        ...state,
        plants: state.plants.map((p) =>
          p.id === action.payload.id ? action.payload : p
        ),
        error: null,
      };
    case 'DELETE_PLANT':
      return {
        ...state,
        plants: state.plants.filter((p) => p.id !== action.payload),
        error: null,
      };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    default:
      return state;
  }
}

function PlantProvider({ children }) {
  const [state, dispatch] = useReducer(plantReducer, initialState);

  const loadPlants = useCallback(async () => {
    try {
      const plants = await db.plants.toArray();
      dispatch({ type: 'LOAD_PLANTS_SUCCESS', payload: plants });
      const species = await sharedDb.plantSpecies.toArray();
      dispatch({ type: 'LOAD_SPECIES_SUCCESS', payload: species });
    } catch (error) {
      dispatch({ type: 'LOAD_PLANTS_ERROR', payload: error.message });
    }
  }, []);

  useEffect(() => {
    loadPlants();
  }, [loadPlants]);

  // Reload species after a sync completes (new species may have been pulled)
  useEffect(() => {
    const handleSyncCompleted = () => loadPlants();
    window.addEventListener('sync-completed', handleSyncCompleted);
    return () => window.removeEventListener('sync-completed', handleSyncCompleted);
  }, [loadPlants]);

  const addSpecies = useCallback(async (speciesData) => {
    const now = new Date().toISOString();
    const newSpecies = {
      ...speciesData,
      id: uuidv4(),
      createdAt: now,
      updatedAt: now,
    };
    await sharedDb.plantSpecies.put(newSpecies);
    dispatch({ type: 'ADD_SPECIES', payload: newSpecies });
    recordChange('plantSpecies', newSpecies.id, 'put');
    return newSpecies;
  }, []);

  const updateSpecies = useCallback(async (id, updates) => {
    const existing = await sharedDb.plantSpecies.get(id);
    if (!existing) throw new Error(`Species with id ${id} not found`);
    const merged = { ...existing, ...updates, id, updatedAt: new Date().toISOString() };
    await sharedDb.plantSpecies.put(merged);
    dispatch({ type: 'UPDATE_SPECIES', payload: merged });
    recordChange('plantSpecies', merged.id, 'put');
    return merged;
  }, []);

  const searchSpecies = useCallback((query) => {
    if (!query || !query.trim()) return state.species;
    const lower = query.toLowerCase();
    return state.species.filter(
      (s) =>
        s.commonName.toLowerCase().includes(lower) ||
        s.scientificName.toLowerCase().includes(lower)
    );
  }, [state.species]);

  const deleteSpecies = useCallback(async (id) => {
    await sharedDb.plantSpecies.delete(id);
    dispatch({ type: 'DELETE_SPECIES', payload: id });
    recordChange('plantSpecies', id, 'delete');
  }, []);

  const addPlant = useCallback(async (plantData) => {
    const now = new Date().toISOString();
    const newPlant = {
      ...plantData,
      id: uuidv4(),
      createdAt: now,
      updatedAt: now,
    };

    // Enforce non-negative quantities
    newPlant.qtyPlugs = Math.max(0, newPlant.qtyPlugs || 0);
    newPlant.qtySoloCups = Math.max(0, newPlant.qtySoloCups || 0);
    newPlant.qtyQuarts = Math.max(0, newPlant.qtyQuarts || 0);
    newPlant.qtyGallons = Math.max(0, newPlant.qtyGallons || 0);

    const validation = validatePlantRecord(newPlant);
    if (!validation.valid) {
      throw new Error(`Validation failed: ${validation.errors.join(', ')}`);
    }

    await db.plants.put(newPlant);
    dispatch({ type: 'ADD_PLANT', payload: newPlant });
    recordChange('plants', newPlant.id, 'put');
    return newPlant;
  }, []);

  const updatePlant = useCallback(async (id, updates) => {
    const existing = await db.plants.get(id);
    if (!existing) {
      throw new Error(`Plant with id ${id} not found`);
    }

    const merged = {
      ...existing,
      ...updates,
      id, // Ensure id cannot be overwritten
      updatedAt: new Date().toISOString(),
    };

    // Enforce non-negative quantities
    merged.qtyPlugs = Math.max(0, merged.qtyPlugs || 0);
    merged.qtySoloCups = Math.max(0, merged.qtySoloCups || 0);
    merged.qtyQuarts = Math.max(0, merged.qtyQuarts || 0);
    merged.qtyGallons = Math.max(0, merged.qtyGallons || 0);

    const validation = validatePlantRecord(merged);
    if (!validation.valid) {
      throw new Error(`Validation failed: ${validation.errors.join(', ')}`);
    }

    // Generate diff for the logbook
    const diffFields = [
      'gridRow', 'gridCol', 'trayNumber', 'source',
      'qtyPlugs', 'qtySoloCups', 'qtyQuarts', 'qtyGallons',
      'commonName', 'scientificName', 'agcpRating', 'empRating',
      'lightRequirements', 'moistureRequirements', 'soilRequirements',
      'bloomSeason', 'bloomColor', 'saltTolerance',
    ];
    const changes = [];
    for (const field of diffFields) {
      const oldVal = existing[field] ?? '';
      const newVal = merged[field] ?? '';
      if (String(oldVal) !== String(newVal)) {
        changes.push({ field, from: oldVal, to: newVal });
      }
    }

    // Auto-create a log entry if there are meaningful changes
    if (changes.length > 0) {
      const changeNotes = changes.map(c => {
        const label = field => {
          const labels = {
            gridRow: 'Row', gridCol: 'Column', trayNumber: 'Tray',
            qtyPlugs: 'Plugs', qtySoloCups: 'Solo Cups', qtyQuarts: 'Quarts', qtyGallons: 'Gallons',
            source: 'Source', commonName: 'Common Name', scientificName: 'Scientific Name',
            agcpRating: 'AGCP Rating', empRating: 'EMP Rating',
            lightRequirements: 'Light', moistureRequirements: 'Moisture',
            soilRequirements: 'Soil', bloomSeason: 'Bloom Season', bloomColor: 'Bloom Color',
          };
          return labels[field] || field;
        };
        const fromStr = c.from === '' || c.from === null || c.from === undefined ? '(empty)' : String(c.from);
        const toStr = c.to === '' || c.to === null || c.to === undefined ? '(empty)' : String(c.to);
        return `${label(c.field)}: ${fromStr} → ${toStr}`;
      }).join('\n');

      const logEntry = {
        id: uuidv4(),
        plantId: id,
        timestamp: merged.updatedAt,
        notes: `[Edit]\n${changeNotes}`,
        photoIds: [],
        type: 'edit',
      };
      await db.logEntries.put(logEntry);
      recordChange('logEntries', logEntry.id, 'put');
    }

    await db.plants.put(merged);
    dispatch({ type: 'UPDATE_PLANT', payload: merged });
    recordChange('plants', merged.id, 'put');
    return merged;
  }, []);

  const deletePlant = useCallback(async (id) => {
    // Remove associated log entries and their photos
    const logEntries = await db.logEntries.where('plantId').equals(id).toArray();
    const photoIds = logEntries.flatMap((entry) => entry.photoIds || []);

    if (photoIds.length > 0) {
      await db.photos.bulkDelete(photoIds);
    }
    if (logEntries.length > 0) {
      await db.logEntries.bulkDelete(logEntries.map((e) => e.id));
    }

    await db.plants.delete(id);
    dispatch({ type: 'DELETE_PLANT', payload: id });
    recordChange('plants', id, 'delete');
  }, []);

  const getPlantsByCell = useCallback(
    (row, col) => {
      return state.plants.filter(
        (plant) =>
          plant.gridRow === row &&
          plant.gridCol === col &&
          ((plant.qtyPlugs || 0) + (plant.qtySoloCups || 0) + (plant.qtyQuarts || 0) + (plant.qtyGallons || 0)) > 0
      );
    },
    [state.plants]
  );

  const getAllPlantsByCell = useCallback(
    (row, col) => {
      return state.plants.filter(
        (plant) => plant.gridRow === row && plant.gridCol === col
      );
    },
    [state.plants]
  );

  const addLogEntry = useCallback(async (plantId, notes, photoBlobs) => {
    const now = new Date().toISOString();
    const photoIds = [];

    // Store each photo blob
    for (const blob of (photoBlobs || [])) {
      const photoId = uuidv4();
      const photo = {
        id: photoId,
        blob,
        mimeType: blob.type || 'image/jpeg',
        createdAt: now,
      };
      await db.photos.put(photo);
      photoIds.push(photoId);
      recordChange('photos', photoId, 'put');
    }

    // Create the log entry
    const logEntry = {
      id: uuidv4(),
      plantId,
      timestamp: now,
      notes: notes || '',
      photoIds,
    };

    await db.logEntries.put(logEntry);
    recordChange('logEntries', logEntry.id, 'put');
    return logEntry;
  }, []);

  const getLogEntries = useCallback(async (plantId) => {
    const entries = await db.logEntries
      .where('plantId')
      .equals(plantId)
      .toArray();
    // Sort by timestamp descending (newest first)
    return entries.sort((a, b) => b.timestamp.localeCompare(a.timestamp));
  }, []);

  const getPhotos = useCallback(async (photoIds) => {
    if (!photoIds || photoIds.length === 0) {
      return [];
    }
    return db.photos.bulkGet(photoIds);
  }, []);

  const value = {
    plants: state.plants,
    species: state.species,
    loading: state.loading,
    error: state.error,
    loadPlants,
    addPlant,
    updatePlant,
    deletePlant,
    getPlantsByCell,
    getAllPlantsByCell,
    addLogEntry,
    getLogEntries,
    getPhotos,
    addSpecies,
    updateSpecies,
    searchSpecies,
    deleteSpecies,
  };

  return <PlantContext.Provider value={value}>{children}</PlantContext.Provider>;
}

export { PlantContext, PlantProvider };
